import { useQuery } from "@tanstack/react-query";
import { useDashboardDate } from "@/contexts/DashboardDateContext";
import { useDashboardDataSource } from "@/contexts/DashboardDataSourceContext";
import { useDashboardAttribution, type AttributionWindow } from "@/contexts/DashboardAttributionContext";
import { DashboardWidget } from "../DashboardWidget";
import { formatCurrency, formatNumber, formatRoas } from "@/lib/formatters";
import type { AttributionRow } from "./types";

const WINDOWS: { key: AttributionWindow; label: string; shortLabel: string }[] = [
  { key: "1d_click", label: "1-Day Click", shortLabel: "1d Click" },
  { key: "7d_click", label: "7-Day Click", shortLabel: "7d Click" },
  { key: "28d_click", label: "28-Day Click", shortLabel: "28d Click" },
  { key: "1d_view", label: "1-Day View", shortLabel: "1d View" },
  { key: "7d_view", label: "7-Day View", shortLabel: "7d View" },
];

interface Props {
  isEditMode: boolean;
  onRemove?: () => void;
  onResize?: () => void;
  onResetSize?: () => void;
}

export function AttributionComparisonTable({ isEditMode, onRemove, onResize, onResetSize }: Props) {
  const { startDate, endDate } = useDashboardDate();
  const { getUrl, dataKey } = useDashboardDataSource();
  const { attributionWindow, setAttributionWindow } = useDashboardAttribution();

  const { data, isLoading, error, refetch } = useQuery<AttributionRow[]>({
    queryKey: ["dashboard-attribution-comparison", dataKey, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams({ startDate, endDate });
      const res = await fetch(getUrl("attribution-comparison", params));
      if (!res.ok) throw new Error("Failed to fetch attribution data");
      return res.json();
    },
  });

  const empty = !isLoading && !error && (!data || data.length === 0);

  return (
    <DashboardWidget title="Attribution Comparison" widgetType="table" isEditMode={isEditMode} isLoading={isLoading} error={error instanceof Error ? error.message : null} onRetry={() => refetch()} onRemove={onRemove} onResize={onResize} onResetSize={onResetSize}>
      {empty ? (
        <div className="flex items-center justify-center h-full text-sm" style={{ color: "#5F6D82" }} data-testid="empty-attribution">No attribution data available</div>
      ) : (
        <div className="p-4 h-full flex flex-col" data-testid="table-attribution">
          <div className="grid grid-cols-5 gap-3 flex-1">
            {WINDOWS.map(w => {
              const row = (data || []).find((d) => d.window === w.key);
              const isSelected = attributionWindow === w.key;
              return (
                <button
                  key={w.key}
                  onClick={() => setAttributionWindow(w.key)}
                  className={`rounded-lg border-2 p-3 text-left transition-all ${
                    isSelected
                      ? "border-indigo-500 bg-indigo-50"
                      : "border-gray-200 hover:border-gray-300 bg-white"
                  }`}
                  data-testid={`attribution-${w.key}`}
                >
                  <p className={`text-[11px] font-semibold mb-2 ${isSelected ? "text-indigo-700" : "text-gray-500"}`}>{w.shortLabel}</p>
                  <p className="text-lg font-bold text-gray-900">{formatNumber(row?.purchases ?? 0)}</p>
                  <p className="text-[11px] text-gray-500">purchases</p>
                  <div className="mt-2 pt-2 border-t border-gray-100">
                    <p className="text-sm font-semibold text-gray-900">{formatRoas(row?.roas ?? 0)}</p>
                    <p className="text-[10px] text-gray-400">ROAS</p>
                  </div>
                  <div className="mt-1">
                    <p className="text-[11px] text-gray-600">{formatCurrency(row?.cpa ?? 0)} CPA</p>
                  </div>
                </button>
              );
            })}
          </div>
          <p className="text-[10px] text-gray-400 mt-3 text-center">
            Default: 7-day click attribution. Click a window to update all dashboard widgets.
          </p>
        </div>
      )}
    </DashboardWidget>
  );
}
